import React, { useState } from 'react';
import {
  Container,
  Paper,
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { getLoginErrorMessage, LoginErrorField } from '../utils/loginErrors';

const PublisherLoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorField, setErrorField] = useState<LoginErrorField>(null);
  const { login, sessionExpiredMessage, clearSessionExpiredMessage } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setErrorField(null);

    try {
      await login(email, password, 'publisher');
      // Navigation will be handled by App.tsx based on role
    } catch (err: unknown) {
      const info = getLoginErrorMessage(err);
      setError(info.message);
      setErrorField(info.field);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%', borderRadius: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                backgroundColor: 'success.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h5" sx={{ color: 'success.main' }}>📝</Typography>
            </Box>
            
            <Typography component="h1" variant="h4" gutterBottom fontWeight="bold">
              Publisher Portal
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Sign in to manage your placements
            </Typography>
            
            {sessionExpiredMessage && !error && (
              <Alert
                severity="warning"
                sx={{ width: '100%', mb: 2, mt: 2 }}
                onClose={clearSessionExpiredMessage}
              >
                {sessionExpiredMessage}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 2, mt: 2 }}>
                {error}
              </Alert>
            )}
            
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2, width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errorField === 'email') setErrorField(null);
                }}
                disabled={loading}
                error={errorField === 'email'}
                helperText={errorField === 'email' ? error : undefined}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errorField === 'password') setErrorField(null);
                }}
                disabled={loading}
                error={errorField === 'password'}
                helperText={errorField === 'password' ? error : undefined}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="success"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
                disabled={loading || !email || !password}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Sign In'
                )}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default PublisherLoginPage;
